import { IFeeData, ISectionData } from 'types/order/fees/ISectionData';
import { eFeePropertyNames } from '../../../../types/order/fees/eFeePropertyNames';
import { ISettlementAndFeeCategoryDefinitionData } from 'types/order/fees/ISettlementAndFeeCategoryDefinition';
import { ISectionDefinitionData } from 'types/order/fees/ISectionDefinition';
import { IOrderTagData } from 'types/order/tags/IOrderTagData';

// These 2 interfaces we actually interact with in code. The others are just containers.
// So I favored pick for these two to show what they are based on, while the rest is more "flexible" and not really based on anything
export interface IValidationDefinition
  extends Pick<
    ISettlementAndFeeCategoryDefinitionData,
    'statementSettlementAndFeeCategoryDefinitionId' | 'stringFeePropertyNames' | 'numericFeePropertyNames'
  > { }

export interface IValidateItemFields extends Pick<IFeeData, 'numericFeePropertyValues' | 'stringFeePropertyValues'> {
  statementSettlementAndFeeCategoryDefinitionId?: string;
}

export interface IValidationSectionData {
  statementSettlementItems: { statementFees: IValidateItemFields[] }[];
}
export interface IValidationLineItem {
  statementFees: IValidateItemFields[];
}
export interface IValidationSectionData {
  statementSettlementItems: IValidationLineItem[];
}
export interface IValidationSection {
  sectionDefinition: ISectionDefinitionData;
  section: ISectionData;
}

interface IValidateItemProps {
  sectionDefinitions: ISectionDefinitionData[];
  sections: ISectionData[];
  tags: IOrderTagData[];
}

export const validateItemAll = ({ sectionDefinitions, sections, tags = [] }: IValidateItemProps): string[] => {
  const getDefinition = (x: ISectionData) => {
    const result = sectionDefinitions.find((t) => t.statementSectionDefinitionId === x.statementSectionDefinitionId);
    return result;
  };
  const results = sections
    .map((section) => ({ section, sectionDefinition: getDefinition(section) }))
    .filter((itemPair) => itemPair.sectionDefinition !== undefined)
    .map((itemPair) => itemPair as IValidationSection)
    .flatMap((section) => validateItemSection(section));

  if (tags.find(tag => tag.tag.label === 'Fees failed to generate')) {
    results.push('Failure to Calculate Underwriter Fees - Manual Calculation Required');
  }

  // Remove duplicates
  return [...new Set(results)];
};

const validateItemSection = ({ section, sectionDefinition }: IValidationSection): string[] => {
  const dataLength = section?.statementSettlementItems?.length || 0;
  if (dataLength === 0) {
    return [];
  }
  return section.statementSettlementItems.flatMap((item) =>
    validateItem(item.statementFees, sectionDefinition.statementSettlementAndFeeCategoryDefinitions)
  );
};

export const validateItem = (fees: IValidateItemFields[], definitions: IValidationDefinition[]): string[] =>
  fees
    .map((fee) => {
      const definition = definitions?.find(
        (t) => t.statementSettlementAndFeeCategoryDefinitionId === fee.statementSettlementAndFeeCategoryDefinitionId
      );
      // If no definition, don't do anything.
      if (!definition) {
        return '';
      }
      if (fee.stringFeePropertyValues[eFeePropertyNames.isPricingValid] === 'false') {
        return 'Failure to Calculate Underwriter Fees - Manual Calculation Required';
      }
      // If the definition doesn't need all 4, don't do anything.
      if (
        !definition.stringFeePropertyNames.includes(eFeePropertyNames.DateFrom) ||
        !definition.stringFeePropertyNames.includes(eFeePropertyNames.DateTo) ||
        !definition.numericFeePropertyNames.includes(eFeePropertyNames.BillingAmount) ||
        !definition.stringFeePropertyNames.includes(eFeePropertyNames.BillingPeriod)
      ) {
        return '';
      }
      const emptyFields = [];
      if (!fee.numericFeePropertyValues[eFeePropertyNames.BillingAmount]) {
        emptyFields.push('Billing Amount');
      }
      if (!fee.stringFeePropertyValues[eFeePropertyNames.BillingPeriod]) {
        emptyFields.push('Billing Period');
      }
      if (!fee.stringFeePropertyValues[eFeePropertyNames.DateFrom]) {
        emptyFields.push('Date From');
      }
      if (!fee.stringFeePropertyValues[eFeePropertyNames.DateTo]) {
        emptyFields.push('Date To');
      }
      return emptyFields.length > 0 && emptyFields.length !== 4
        ? emptyFields.join(', ') + ' are required to successfully generate a CD/Settlement Item Statement.'
        : '';
    })
    .filter((warning) => warning !== '');
